import React from "react";
import { FEATURES } from "@/constants/testIds";
import {
  SendHorizontal,
  ShieldCheck,
  Clock4,
  Building2,
  BarChart3,
  Workflow,
} from "lucide-react";

const AGING_IMG =
  `${process.env.PUBLIC_URL}/images/aging.png`;

const cards = [
  {
    testId: FEATURES.cardClaims,
    icon: SendHorizontal,
    eyebrow: "01 · Submission",
    title: "Claim submission, on autopilot",
    body:
      "AI agents draft, validate and submit clean claims to payers — checking eligibility, modifiers, and code combinations before they ever leave your desk.",
    span: "md:col-span-2",
  },
  {
    testId: FEATURES.cardDenials,
    icon: ShieldCheck,
    eyebrow: "02 · Denial Prevention",
    title: "Stop denials before they happen",
    body:
      "A learning policy engine catches the issues each payer cares about — coverage, auth, NPI mismatches — and routes them to the right human.",
    span: "md:col-span-2",
  },
  {
    testId: FEATURES.cardAging,
    icon: Clock4,
    eyebrow: "03 · Aging Intelligence",
    title: "Real-time aging tracking",
    body:
      "Watch every dollar move from Session Amount to Collected. Aging buckets are computed continuously, not at month-end.",
    span: "md:col-span-3",
    image: AGING_IMG,
  },
  {
    testId: FEATURES.cardMultitenant,
    icon: Building2,
    eyebrow: "04 · Multi-tenant",
    title: "One platform, every practice",
    body:
      "Designed for billing companies and group practices. Filter by Billing Co · Practice · Insurance and see consolidated revenue in seconds.",
    span: "md:col-span-1",
  },
];

const mini = [
  { icon: BarChart3, label: "Live KPIs across every claim status" },
  { icon: Workflow, label: "Native EHR / PMS connections" },
  { icon: ShieldCheck, label: "HIPAA-aligned audit trail" },
];

export const Features = () => {
  return (
    <section
      id="features"
      data-testid={FEATURES.section}
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="font-mono-label text-[10px] text-[#0047FF]">
            // The platform
          </span>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-950">
            Everything your revenue cycle needs.{" "}
            <span className="text-zinc-400">Nothing it doesn&apos;t.</span>
          </h2>
          <p className="mt-5 text-lg text-zinc-600 max-w-2xl">
            Zaplee.ai replaces the patchwork of clearinghouses, spreadsheets and
            manual follow-up with one AI-native workspace built for healthcare RCM.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-6">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.testId}
                data-testid={c.testId}
                className={`group ${c.span} rounded-2xl border border-zinc-200 bg-white p-7 transition-all duration-300 hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-[0_18px_50px_-25px_rgba(9,9,11,0.18)]`}
              >
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-xl bg-zinc-950 text-white grid place-items-center">
                    <Icon size={18} />
                  </div>
                  <span className="font-mono-label text-[10px] text-zinc-400">
                    {c.eyebrow}
                  </span>
                </div>
                <h3 className="font-display mt-6 text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950">
                  {c.title}
                </h3>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                  {c.body}
                </p>

                {c.image && (
                  <div className="mt-6 rounded-xl border border-zinc-200 overflow-hidden bg-white">
                    <img
                      src={c.image}
                      alt="Aging report"
                      className="w-full block"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {mini.map((m, i) => {
            const Icon = m.icon;
            return (
              <div
                key={i}
                className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-5 py-4"
              >
                <span className="h-8 w-8 rounded-lg bg-[#0047FF]/10 text-[#0047FF] grid place-items-center">
                  <Icon size={16} />
                </span>
                <span className="text-sm text-zinc-700">{m.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
